import { isRSC, isWeb } from "@tamagui/constants";
import { configListeners, setConfig } from "./config.js";
import { createVariables, tokensKeysOrdered } from "./createVariables.js";
import { getThemeCSSRules } from "./helpers/getThemeCSSRules.js";
import {
  getAllRules,
  listenForSheetChanges,
  scanAllSheets
} from "./helpers/insertStyleRule.js";
import {
  registerCSSVariable,
  tokensValueToVariable,
  variableToCSS
} from "./helpers/registerCSSVariable.js";
import { ensureThemeVariable, proxyThemeToParents } from "./helpers/themes.js";
import { configureMedia } from "./hooks/useMedia.js";
import { parseFont, registerFontVariables } from "./insertFont.js";
import { Tamagui } from "./Tamagui.js";
const createdConfigs = /* @__PURE__ */ new WeakMap();
function createTamagui(configIn) {
  var _a;
  if (createdConfigs.has(configIn)) {
    return configIn;
  }
  if (process.env.NODE_ENV === "development") {
    if (!configIn.tokens) {
      throw new Error("Must define tokens");
    }
    if (!configIn.themes) {
      throw new Error("Must define themes");
    }
    if (!configIn.fonts) {
      throw new Error("Must define fonts");
    }
  }
  scanAllSheets();
  listenForSheetChanges();
  const fontTokens = Object.fromEntries(
    Object.entries(configIn.fonts).map(([k, v]) => {
      return [k, createVariables(v, "f", true)];
    })
  );
  const fontsParsed = (() => {
    const res = {};
    for (const familyName in fontTokens) {
      res[`$${familyName}`] = parseFont(fontTokens[familyName]);
    }
    return res;
  })();
  const themeConfig = (() => {
    const themes = { ...configIn.themes };
    const cssRuleSets = [];
    if (isWeb) {
      let declarationsToRuleSet2 = function(decs, selector = "") {
        return `:root${selector} {${sep}${[...decs].join(`;${sep}`)}${sep}}`;
      };
      var declarationsToRuleSet = declarationsToRuleSet2;
      const declarations = [];
      const fontDeclarations = {};
      for (const key in configIn.tokens) {
        for (const skey in configIn.tokens[key]) {
          const val = configIn.tokens[key][skey];
          registerCSSVariable(val);
          declarations.push(variableToCSS(val));
        }
      }
      for (const key in fontsParsed) {
        const fontParsed = fontsParsed[key];
        const [name, language] = key.includes("_") ? key.split("_") : [key];
        const fontVars = registerFontVariables(fontParsed);
        fontDeclarations[key] = {
          name: name.slice(1),
          declarations: fontVars,
          language
        };
      }
      const sep = process.env.NODE_ENV === "development" ? configIn.cssStyleSeparator || " " : "";
      cssRuleSets.push(declarationsToRuleSet2(declarations));
      if (fontDeclarations) {
        for (const key in fontDeclarations) {
          const { name, declarations: declarations2, language = "default" } = fontDeclarations[key];
          const fontSelector = `.font_${name}`;
          const langSelector = `:root .t_lang-${name}-${language} ${fontSelector}`;
          const selectors = language === "default" ? ` ${fontSelector}, ${langSelector}` : langSelector;
          const specificRuleSet = declarationsToRuleSet2(declarations2, selectors);
          cssRuleSets.push(specificRuleSet);
        }
      }
    }
    const dedupedThemes = {};
    const existing = /* @__PURE__ */ new Map();
    for (const themeName in themes) {
      const rawTheme = themes[themeName];
      const key = JSON.stringify(rawTheme);
      if (existing.has(key)) {
        const e = existing.get(key);
        themes[themeName] = e.theme;
        e.names.push(themeName);
        continue;
      }
      const theme = { ...rawTheme };
      for (const key2 in theme) {
        ensureThemeVariable(theme, key2);
      }
      themes[themeName] = theme;
      dedupedThemes[themeName] = {
        names: [themeName],
        theme
      };
      existing.set(key, dedupedThemes[themeName]);
    }
    let themeRuleSets = [];
    if (isWeb || isRSC) {
      for (const themeName in dedupedThemes) {
        themeRuleSets = [
          ...themeRuleSets,
          ...getThemeCSSRules({
            config: configIn,
            themeName,
            ...dedupedThemes[themeName]
          })
        ];
      }
    }
    for (const themeName in themes) {
      themes[themeName] = proxyThemeToParents(themeName, themes[themeName], themes);
    }
    tokensValueToVariable.clear();
    return {
      themes,
      cssRuleSets,
      themeRuleSets
    };
  })();
  const tokensParsed = Object.fromEntries(
    Object.entries(configIn.tokens).map(([k, v]) => {
      const val = Object.fromEntries(Object.entries(v).map(([k2, v2]) => [`$${k2}`, v2]));
      tokensKeysOrdered.set(val, tokensKeysOrdered.get(v));
      return [k, val];
    })
  );
  const shorthands = configIn.shorthands || {};
  const config = {
    fontLanguages: [],
    animations: {},
    media: {},
    ...configIn,
    // already processed by createTokens()
    tokens: configIn.tokens,
    // vite made this into a function if it wasn't set
    shorthands: { ...shorthands },
    inverseShorthands: shorthands ? Object.fromEntries(Object.entries(shorthands).map(([k, v]) => [v, k])) : {},
    themes: themeConfig.themes,
    fontsParsed,
    themeConfig,
    tokensParsed,
    parsed: true,
    getCSS: (separator = "\n") => {
      return `
._ovs-contain {overscroll-behavior:contain;}
.t_unmounted .t_will-mount {opacity:0;visibility:hidden;}
.is_Text .is_Text {display:inline-flex;}
._dsp_contents {display:contents;}
${themeConfig.cssRuleSets.join(separator)}
${themeConfig.themeRuleSets.join(separator)}
${getAllRules().join(separator)}`;
    }
    // const tokens = [...getToken(tokens.size[0])]
    // .spacer-sm + ._dsp_contents._dsp-sm-hidden { margin-left: -var(--${}) }
  };
  configureMedia(config);
  setConfig(config);
  if (configListeners.size) {
    configListeners.forEach((cb) => cb(config));
    configListeners.clear();
  }
  createdConfigs.set(config, true);
  if (process.env.NODE_ENV === "development") {
    if ((_a = process.env.DEBUG) == null ? void 0 : _a.startsWith("tamagui")) {
      console.log("Tamagui config:", config);
    }
    if (!globalThis["Tamagui"]) {
      globalThis["Tamagui"] = Tamagui;
    }
  }
  return config;
}
export {
  createTamagui
};
//# sourceMappingURL=createTamagui.js.map
